import { TierLabel } from '@membership/models/Subscription';
import { getLocalizedNYAppPromoPrice } from '@membership/pricing/utils/getLocalizedNYAppPromoPrice';
import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { APP_ONE_MTM_SKU } from '@peloton/subscription-plans/skus';
import { toCloudinaryUrl } from '@engage/cloudinary/toCloudinaryUrl';
import { white } from '@engage/colors';
import { useReduxState } from '@engage/redux';
import { spaces } from '@engage/styles';
import { getOwnSubscriptions, getActiveSubs } from '@engage/subscriptions';
import { label12 } from '@engage/typography';
import { track } from '@members/analytics';
import { button1Styles, smallButtonStyles } from '@members/buttons';
import { TieringCopy, SubscriptionsCopy } from '@members/copy';
import { Subscriptions } from '@members/copy/models.generated/Subscriptions';
import { Tiering } from '@members/copy/models.generated/Tiering';
import {
  useFeatureToggle,
  useFeatureVariables,
} from '@members/feature-toggles/featureToggles';
import { Feature } from '@members/feature-toggles/toggles';
import { CLASS_PLAY_BUTTON_SOURCE } from '@members/global-button-handlers/classPlayButtonSource';
import { useOnScreen } from '@members/scroll/useOnScreen';
import { useTierContext } from '@members/tiering';
import { useLocalizedMonthlyMembershipPrice } from '@members/tiering/hooks/useLocalizedMonthlyMembershipPrice';
import { useUpgradeOrReactivate } from '@members/tiering/hooks/useUpgradeOrReactivate';
import { BackgroundImageContainer, Title as PrevTitle } from '../ClassPreviewCard';
import { VideoPreview } from './../VideoPreview';
import { trackViewedBannerCard } from './analytics';
import { getCopyIdByDiscipline } from './helpers';

const TRACKING_PARAMS = {
  source: 'Browse Banner Upsell',
};

export const UpsellEncouragementCard: React.FC<
  React.PropsWithChildren<{
    imageUrl: string;
    fitnessDiscipline: string;
    rideId: string;
    scrollRef: any;
  }>
> = ({ imageUrl, rideId, fitnessDiscipline, scrollRef }) => {
  const subs = useReduxState(getOwnSubscriptions);
  const hasActiveSub = Boolean(getActiveSubs(subs).length);
  const hasSubsNoActiveSub = subs && subs.length > 0 && !hasActiveSub;
  const [isVideoReady, setIsVideoReady] = React.useState(false);
  const currentStartTime = useMemo(() => new Date().toISOString(), []);
  const [isStickyDogCycleEnabled, { trialLengthInDays }] = useFeatureVariables(
    Feature.Stickydogcycle,
  );
  const isNyAppPromo = useFeatureToggle(Feature.Ny_app_promo);
  const { isEligibleForFreeTrial } = useTierContext();
  const upgradePath = useUpgradeOrReactivate(false, TRACKING_PARAMS);
  const lengthOfTrial = isStickyDogCycleEnabled ? trialLengthInDays : 30;
  const monthlyPrice = useLocalizedMonthlyMembershipPrice(APP_ONE_MTM_SKU);
  const promoMonthlyPrice = getLocalizedNYAppPromoPrice(TierLabel.DIGITAL_BASIC);
  const monthlyPriceAfterPromo = useLocalizedMonthlyMembershipPrice(APP_ONE_MTM_SKU);
  const titleCopy = getCopyIdByDiscipline(fitnessDiscipline);
  let buttonCopy;
  let tryMembershipCopy;
  if (isNyAppPromo) {
    buttonCopy = (
      <TieringCopy id={hasSubsNoActiveSub ? Tiering.Reactivate : Tiering.Upgrade} />
    );
    tryMembershipCopy = (
      <TieringCopy
        id={Tiering.TryMembershipNyAppPromo}
        values={{
          monthlyPrice: promoMonthlyPrice,
          monthlyPriceAfter: monthlyPriceAfterPromo,
        }}
      />
    );
  } else if (isEligibleForFreeTrial) {
    buttonCopy = (
      <TieringCopy
        id={Tiering.StartOneMonthTrial}
        values={{ numberOfDays: lengthOfTrial }}
      />
    );
    tryMembershipCopy = (
      <TieringCopy
        id={Tiering.TryMembership}
        values={{ monthlyPrice, numberOfDays: lengthOfTrial }}
      />
    );
  } else {
    if (hasSubsNoActiveSub) {
      buttonCopy = <TieringCopy id={Tiering.Reactivate} />;
    } else {
      buttonCopy = <SubscriptionsCopy id={Subscriptions.UpgradeNowCta} />;
    }

    tryMembershipCopy = <TieringCopy id={Tiering.MoreClasses} />;
  }

  // -5% indicates the amount of the card that is seen before the event fires.
  const [onScreen, unobserve] = useOnScreen(scrollRef, `-5%`, '');
  useEffect(() => {
    if (onScreen) {
      track(trackViewedBannerCard(fitnessDiscipline, titleCopy));
      unobserve.current();
    }
  }, [fitnessDiscipline, titleCopy, onScreen, unobserve]);
  return (
    <StyledLink href={upgradePath}>
      <BackgroundImageContainer
        data-test-id="videoCell"
        backgroundImage={toCloudinaryUrl(imageUrl || '', {
          width: 512,
        })}
        hasRelevantClassInfo={false}
        shouldFadeOut={isVideoReady}
      >
        <VideoPreview
          rideId={rideId}
          imageUrl={imageUrl}
          currentStartTime={currentStartTime}
          source={CLASS_PLAY_BUTTON_SOURCE.NONE}
          onReadyCallback={() => {
            setIsVideoReady(true);
          }}
        />
        <InfoContainer data-test-id="encouragementCardContainer">
          <Title data-test-id="encouragementCardtitle">
            <TieringCopy id={titleCopy} />
          </Title>
          <Subtitle data-test-id="encouragementCardSubtitle">
            {tryMembershipCopy}
          </Subtitle>
          <Button data-test-id="trialUpgradeButton" href={upgradePath}>
            {buttonCopy}
          </Button>
        </InfoContainer>
      </BackgroundImageContainer>
    </StyledLink>
  );
};

const InfoContainer = styled.div`
  position: absolute;
  width: 299px;
  bottom: ${spaces.small}px;
  left: ${spaces.small}px;
  right: ${spaces.small}px;
  color: ${white};
`;

const Title = styled(PrevTitle)`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: ${spaces.xxxSmall}px;
`;

const Subtitle = styled.p`
  ${label12}
  margin-bottom: ${spaces.medium}px;
  font-weight: 600;
  line-height: 16px;
`;

const Button = styled.a`
  ${button1Styles}
  ${smallButtonStyles}
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  cursor: pointer;
  background: var(--app, linear-gradient(90deg, #ff3347 51.56%, #fc820f 100%));
  ${label12}
  font-weight: 600;
  line-height: 16px;
  width: 120px;
`;

const StyledLink = styled.a`
  ${BackgroundImageContainer} {
    filter: brightness(115%);
  }
`;
